// @ts-strict-ignore
import { IOperation } from './operation/i-operation';
import { SequenceOperation } from './operation/sequence-operation';
import { EventEmitter, Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class UndoRedoManager {
  protected undoStack: IOperation[] = [];
  protected redoStack: IOperation[] = [];

  private _undoAndRedoItemLimit: number = 25;

  private _currentOperations: IOperation[] = [];
  private _pushOperationsToSequence: number = 0;

  public undoStackChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  public redoStackChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  public clearAll(): void {
    this._pushOperationsToSequence = 0;
    if (this._currentOperations) {
      this._currentOperations.length = 0;
    }
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.dispatchUndoStackChanged();
    this.dispatchRedoStackChanged();
  }

  public canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  public peekUndo(): IOperation {
    if (this._pushOperationsToSequence) {
      return this._currentOperations.length > 0 ? this._currentOperations[this._currentOperations.length - 1] : null;
    } else {
      return this.undoStack.length > 0 ? this.undoStack[this.undoStack.length - 1] : null;
    }
  }

  public popUndo(): IOperation {
    if (this._pushOperationsToSequence) {
      return this._currentOperations.pop();
    } else {
      const operation: IOperation = this.undoStack.pop();
      this.dispatchUndoStackChanged();
      return operation;
    }
  }

  public popUndosToIndex(index: number) {
    if (this._pushOperationsToSequence) {
      return this._currentOperations.splice(this._currentOperations.length - 1 - index);
    } else {
      return this.undoStack.splice(this.undoStack.length - 1 - index);
    }
  }

  protected doPushUndo(operation: IOperation): void {
    if (this._pushOperationsToSequence) {
      this._currentOperations.push(operation);
    } else {
      this.undoStack.push(operation);
      this.trimUndoRedoStacks();
    }
  }

  protected doPushUndos(operations: IOperation[]): void {
    if (this._pushOperationsToSequence) {
      this._currentOperations.push(...operations);
    } else {
      this.undoStack.push(...operations);
      this.trimUndoRedoStacks();
    }
  }

  public canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  public clearRedo(): void {
    this.redoStack.length = 0;
    this.dispatchRedoStackChanged();
  }

  public peekRedo(): IOperation {
    return this.redoStack.length > 0 ? this.redoStack[this.redoStack.length - 1] : null;
  }

  public popRedo(): IOperation {
    const operation: IOperation = this.redoStack.pop();
    this.dispatchRedoStackChanged();
    return operation;
  }

  public popRedosToIndex(index: number): IOperation[] {
    return this.redoStack.splice(this.redoStack.length - 1 - index);
  }

  protected doPushRedo(operation: IOperation): void {
    this.redoStack.push(operation);
    this.trimUndoRedoStacks();
  }

  protected doPushRedos(operations: IOperation[]) {
    this.redoStack.push(...operations);
    this.trimUndoRedoStacks();
  }

  public get undoAndRedoItemLimit(): number {
    return this._undoAndRedoItemLimit;
  }

  public set undoAndRedoItemLimit(value: number) {
    this._undoAndRedoItemLimit = value;
    this.trimUndoRedoStacks();
  }

  public async undo(): Promise<void> {
    this.finalizeUndoOperationSequence();
    await this.internalUndo(true);
  }

  public async undoN(index: number): Promise<void> {
    this.finalizeUndoOperationSequence();
    await this.internalUndoN(index, true);
  }

  private async internalUndo(pushUndoneOperationsToRedoStack: boolean = true): Promise<void> {
    if (this.canUndo()) {
      const undoOp: IOperation = this.popUndo();
      await undoOp.performUndo();
      if (pushUndoneOperationsToRedoStack) {
        this.doPushRedo(undoOp);
      }
    }
  }

  private async internalUndoN(index: number, pushUndoneOperationsToRedoStack: boolean = true): Promise<void> {
    if (this.canUndo()) {
      const undoOps: IOperation[] = this.popUndosToIndex(index).reverse();
      for (let undoOp of undoOps) {
        await undoOp.performUndo();
      }
      if (pushUndoneOperationsToRedoStack) {
        this.doPushRedos(undoOps);
      }
    }
  }

  public async redo(): Promise<void> {
    this.finalizeUndoOperationSequence();
    if (this.canRedo()) {
      const redoOp: IOperation = this.popRedo();
      await redoOp.performRedo();
      this.doPushUndo(redoOp);
    }
  }

  public async redoN(index: number): Promise<void> {
    this.finalizeUndoOperationSequence();
    if (this.canRedo()) {
      const redoOps: IOperation[] = this.popRedosToIndex(index).reverse();
      for (let redoOp of redoOps) {
        await redoOp.performRedo();
      }
      this.doPushUndos(redoOps);
    }
  }

  public peekRedos(limit: number): IOperation[] {
    if (length > limit) {
      return this.redoStack.slice(this.redoStack.length - 1 - limit);
    } else {
      return this.redoStack;
    }
  }

  public peekUndos(limit: number): IOperation[] {
    if (length > limit) {
      return this.undoStack.slice(this.undoStack.length - 1 - limit);
    } else {
      return this.undoStack;
    }
  }

  /** trim the sizes of the undo/redo stacks to the maximum limits */
  private trimUndoRedoStacks(): void {
    // trim the undoStack and the redoStack so its in bounds
    let numItems: number = this.undoStack.length + this.redoStack.length;
    if (numItems > this._undoAndRedoItemLimit) {
      // trim redoStack first
      let numToSplice: number = Math.min(numItems - this._undoAndRedoItemLimit, this.redoStack.length);
      if (numToSplice) {
        this.redoStack.splice(0, numToSplice);
        numItems = this.undoStack.length + this.redoStack.length;
      }
      // trim some undoable items
      if (numItems > this._undoAndRedoItemLimit) {
        numToSplice = Math.min(numItems - this._undoAndRedoItemLimit, this.undoStack.length);
        this.undoStack.splice(0, numToSplice);
      }
    }
    this.dispatchRedoStackChanged();
    this.dispatchUndoStackChanged();
  }

  public pushUndo(undo: IOperation): void {
    if (this.canUndo()) {
      // get last operation and check on equals
      const lastUndo: IOperation = this.peekUndo();
      if (this.areEquals(undo, lastUndo)) {
        return;
      }
    }
    // clear redo stack before pushing new undo operation
    this.clearRedo();
    // push undo
    this.doPushUndo(undo);
  }

  public pushRedo(redo: IOperation): void {
    this.finalizeUndoOperationSequence();
    if (this.canRedo()) {
      // get last operation and check on equals
      const lastRedo: IOperation = this.peekRedo();
      if (this.areEquals(redo, lastRedo)) {
        return;
      }
    }
    // push redo
    this.doPushRedo(redo);
  }

  protected areEquals(newOper: any, oldOper: any): boolean {
    if (newOper && newOper.equals !== undefined && oldOper && oldOper.equals !== undefined) {
      return newOper.equals(oldOper);
    } else {
      return newOper === oldOper;
    }
  }

  public startUndoOperationSequence(): void {
    if (!this._pushOperationsToSequence) {
      this._currentOperations = [];
    }
    this._pushOperationsToSequence++;
  }

  /**
   * @returns true if an undo operation was pushed, false otherwise.
   */
  public finalizeUndoOperationSequence(additionalOperations: IOperation[] = []): boolean {
    if (this._pushOperationsToSequence) {
      const currentOperations = this._currentOperations;
      this._pushOperationsToSequence--;
      if (this._pushOperationsToSequence === 0) {
        this._currentOperations = null;
        if (currentOperations.length > 0) {
          this.doPushUndo(new SequenceOperation(currentOperations, additionalOperations));
          return true;
        }
      }
    }
    return false;
  }

  private dispatchUndoStackChanged(): void {
    this.undoStackChanged.emit(this.canUndo());
  }

  private dispatchRedoStackChanged(): void {
    this.redoStackChanged.emit(this.canRedo());
  }

  public filterUndo(filterFunction: Function ): void {
    this.undoStack = this.undoStack.filter((o) => filterFunction(o));
    this.dispatchUndoStackChanged();

  }

  public async doWithSequence<T>(asyncFunc: (pushOperation: (undo: IOperation) => void) => Promise<T>): Promise<T> {
    this.startUndoOperationSequence();
    try {
      return await asyncFunc(this.pushUndo.bind(this));
    } catch (error) {
      if (this.finalizeUndoOperationSequence()) {
        await this.popUndo().performUndo();
      }
      throw error;
    } finally {
      this.finalizeUndoOperationSequence();
    }
  }

  public popAllFromRunningSequence(): IOperation[] {
    if (this._pushOperationsToSequence) {
      const currentOperations = this._currentOperations;
      this._currentOperations = [];
      return currentOperations;
    }
    return [];
  }
}
